'use client';

import { useEffect, useState } from 'react';
import { ChevronDown, Loader2, MessageSquareQuote } from 'lucide-react';

import { ReviewHistogram, ReviewStars } from '@/components/reviews/ReviewStars';
import { ReviewListItem } from '@/components/reviews/ReviewListItem';
import type { ReviewsSummary } from '@/components/reviews/types';
import { cn } from '@/lib/utils';

// Reviews section for the public menu page (doc 60 §4.2): a collapsible card
// near the footer, template-palette neutral so it sits on any menu skin. The
// summary row is always visible; the histogram + recent reviews expand on
// demand. All data is fetched client-side after mount -- the summary is never
// in the server-rendered HTML, so there is no hydration mismatch.

const MAX_SHOWN = 5;

export function MenuReviewsSection({ branchId, businessName }: { branchId: string; businessName?: string | null }) {
  const [data, setData] = useState<ReviewsSummary | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    fetch(`/api/public/places/${branchId}/reviews`)
      .then((r) => r.json())
      .then((json: ReviewsSummary) => {
        if (cancelled) return;
        if (!json?.ok) throw new Error('Request failed');
        setData(json);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [branchId]);

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-[var(--color-text-secondary)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading reviews
      </div>
    );
  }

  if (state === 'error') {
    return null;
  }

  // Doc 60 D4: the merchant turned reviews off for this branch -- hide the
  // section entirely, distinct from "no reviews yet".
  if (data?.reviewsEnabled === false) {
    return null;
  }

  const total = data?.totalCount ?? 0;

  if (total === 0) {
    return (
      <div className="rounded-2xl border border-[var(--dm-card-border)] bg-[var(--dm-card-bg)] px-4 py-6 text-center">
        <MessageSquareQuote className="mx-auto size-6 text-[var(--color-text-secondary)]/50" aria-hidden />
        <p className="mt-2 text-sm font-medium">No reviews yet</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-[var(--color-text-secondary)]">
          This place has not been reviewed on Menuraq yet.
        </p>
      </div>
    );
  }

  const shown = (data?.reviews ?? []).slice(0, MAX_SHOWN);

  return (
    <section className="rounded-2xl border border-[var(--dm-card-border)] bg-[var(--dm-card-bg)]">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className="text-xl font-bold leading-none tabular-nums">{data?.avgRating?.toFixed(1)}</p>
            <ReviewStars value={data?.avgRating ?? 0} className="mt-1" />
          </div>
          <div>
            <p className="text-sm font-semibold">Reviews</p>
            <p className="text-xs text-[var(--color-text-secondary)]">
              {total} {total === 1 ? 'rating' : 'ratings'}
            </p>
          </div>
        </div>
        <ChevronDown className={cn('size-4 shrink-0 text-[var(--color-text-secondary)] transition-transform duration-200', expanded && 'rotate-180')} aria-hidden />
      </button>

      {expanded ? (
        <div className="border-t border-[var(--dm-card-border)] px-4 pb-4 pt-4">
          <ReviewHistogram breakdown={data?.breakdown ?? {}} total={total} className="mb-4 max-w-md" />
          <ul className="divide-y divide-border">
            {shown.map((review) => (
              <ReviewListItem key={review.id} review={review} businessName={businessName} />
            ))}
          </ul>
          {total > MAX_SHOWN ? (
            <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
              Showing {MAX_SHOWN} of {total} reviews.
            </p>
          ) : null}
          <p className="mt-3 text-2xs text-[var(--color-text-secondary)]/70">
            Post-payment reviews are tied to a paid order and show as verified purchases.
          </p>
        </div>
      ) : null}
    </section>
  );
}

export default MenuReviewsSection;
